// IMP-221: 라이선스 코드 SSoT (Confluence "Local License Check API doc" 2045050896 미러).
// BUG-113 PR #240 Arch Major — Confluence 표 ↔ FE 매핑 상수 ↔ i18n 키 3곳 분산 drift 차단.
// L0001/L0002/L0003(active, check===true) 은 의도적으로 badge 미매핑 — Deactivate 버튼이
// 활성 상태를 안내하므로 정보성 배지 중복(RTSQA-339 round 18). getLicenseCodeBadge fail-closed.
// IMP-421 (#356): `messageKey` — 활성화 **실패 시** 사용자 안내 문구의 i18n 키. 종전에는
// `usePcActivation.handleActivate` 가 실패 시 코드 문자열(`A0001` 등)을 그대로 노출해 원인·조치를
// 알 수 없었다. 배지(`badge`)를 가진 코드는 그 문구가 이미 사용자 대면 문장이므로 **원칙적으로
// 동일 키를 재사용**한다(문구 이중 관리 = drift 원인). 단 배지가 긍정 상태를 안내하는 코드(N0008)는
// 실패 배너에서 의미가 반전되므로 실패 전용 키를 둔다. 성공 계열(L0001~L0003)은 실패 메시지 대상이 아니라
// messageKey 를 두지 않는다.
export const LICENSE_CODES = {
    L0001: { active: true },
    L0002: { active: true },
    L0003: { active: true },
    A0001: { messageKey: 'pcActivation.code.A0001' },
    // A0002 도 `check===true` 지만 활성 코드가 아니어서 BUG-164 가 실패로 강등한다
    // (PcActivationAction.js) → 매핑이 없으면 `A0002 — 오류` 폴백으로 남는다(리뷰 Minor).
    // 코드표상 N0004/N0006 은 결번(문서에 미정의)이라 매핑 대상이 아니다.
    A0002: { messageKey: 'pcActivation.code.A0002' },
    L0004: { messageKey: 'pcActivation.code.L0004' },
    N0000: { messageKey: 'pcActivation.code.N0000' },
    N0001: { messageKey: 'pcActivation.code.N0001' },
    N0002: { messageKey: 'pcActivation.code.N0002' },
    N0003: { badge: { i18nKey: 'pcActivation.code.N0003', variant: 'info' }, messageKey: 'pcActivation.code.N0003' },
    N0005: { messageKey: 'pcActivation.code.N0005' },
    N0007: { messageKey: 'pcActivation.code.N0007' },
    // N0008 만 배지 키를 재사용하지 않는다(리뷰 Major) — 배지 문구는 "이 PC는 활성화 가능
    // 상태입니다"(긍정 상태 안내)라서 실패 배너에 그대로 쓰면 "왜 실패했는지" 대신 성공
    // 가능하다는 인상을 준다. 실패 문맥 전용 키로 분리한다.
    N0008: { badge: { i18nKey: 'pcActivation.code.N0008', variant: 'info' }, messageKey: 'pcActivation.code.N0008.failed' },
    N0009: { badge: { i18nKey: 'pcActivation.code.N0009', variant: 'danger' }, blocksActivation: true, messageKey: 'pcActivation.code.N0009' },
    N0010: { messageKey: 'pcActivation.code.N0010' },
    N0011: { badge: { i18nKey: 'pcActivation.code.N0011', variant: 'warning' }, blocksActivation: true, messageKey: 'pcActivation.code.N0011' },
    N0012: { badge: { i18nKey: 'pcActivation.code.N0012', variant: 'danger' }, blocksActivation: true, messageKey: 'pcActivation.code.N0012' },
    // E 계열 — 서버/요청/설정 오류. 배지 대상은 아니며 실패 안내 문구만 가진다.
    E0001: { messageKey: 'pcActivation.code.E0001' },
    E0002: { messageKey: 'pcActivation.code.E0002' },
    E0003: { messageKey: 'pcActivation.code.E0003' },
};

export const LICENSE_CODE_BADGES = Object.fromEntries(
    Object.entries(LICENSE_CODES).filter(([, m]) => m.badge).map(([code, m]) => [code, m.badge]),
);

// PR #240 review (Sec Major + Arch Minor): defense-in-depth — 자격 차단 코드.
//   N0009: Activation count limit / N0011: Seat limit / N0012: Suspended.
//   (N0003 No registered licenses 는 isVoucherUnregistered 로 별도 처리)
export const ACTIVATION_BLOCKED_CODES = new Set(
    Object.entries(LICENSE_CODES).filter(([, m]) => m.blocksActivation).map(([code]) => code),
);

// `code` 는 신뢰 경계 밖(localhost:8008 rayteams 응답)에서 온다. 그냥 인덱싱하면
// `code === 'constructor'` 같은 입력이 프로토타입 체인을 타 truthy 를 반환해 fail-open 이 된다
// (리뷰 Suggestion). own-property 만 조회한다.
const ownLookup = (table, code) =>
    (typeof code === 'string' && Object.prototype.hasOwnProperty.call(table, code) ? table[code] : null);

export const getLicenseCodeBadge = (localLicenseStatus) => {
    if (!localLicenseStatus || typeof localLicenseStatus !== 'object') return null;
    // fail-closed: 미정의 code 시 null (BE/FE SSoT drift 시 "active" 오표시 차단, fail-loud).
    return ownLookup(LICENSE_CODE_BADGES, localLicenseStatus.code) ?? null;
};

export const isActivationBlocked = (code) => ACTIVATION_BLOCKED_CODES.has(code);

// BUG-164: PC 활성화 성공 판정 SSoT. rayteams 코드표상 check===true 를 반환하는 코드는
// A0001/A0002/L0001~L0003 이나, 실제 활성 상태를 뜻하는 코드는 L0001/L0002/L0003 뿐이다
// (A0001 은 "관리하지 않는 app" 이라 무조건 true — 활성 아님. A0002 는 app 정보 반환).
// IMP-421 이 A0001 에 `messageKey` 를 추가했지만 `active` 플래그는 없으므로 이 집합에서 자동
// 제외된다 — 판정은 `active` 플래그 기준이며 등록 여부가 아니다.
export const ACTIVE_LICENSE_CODES = new Set(
    Object.entries(LICENSE_CODES).filter(([, m]) => m.active).map(([code]) => code),
);

export const isActiveLicenseCode = (code) => ACTIVE_LICENSE_CODES.has(code);

// IMP-421 (#356): 활성화 실패 코드 → 사용자 안내 문구 i18n 키. 미정의 코드는 `null`(fail-closed)
// — BE/FE 코드표 drift 시 엉뚱한 안내를 보여주는 것보다 코드 원문 + 일반 안내로 폴백하는 편이
// 낫다(호출처 `usePcActivation.handleActivate` 가 그 폴백을 담당).
export const getLicenseCodeMessageKey = (code) => ownLookup(LICENSE_CODES, code)?.messageKey ?? null;
